import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/shared/state/authstore';
import { credentialApi } from '../api/credentialApi';
import { requiredCredentialApi } from '../api/requiredCredentialApi';
import { credentialMappingApi } from '../api/credentialMappingApi';
import { credentialComplianceApi } from '../api/credentialComplianceApi';
import { credentialValidityApi } from '../api/credentialValidityApi';
import type {
  CreateEmployeeCredentialRequest,
  UpdateEmployeeCredentialRequest,
  CreateRequiredCredentialRequest,
  UpdateRequiredCredentialRequest,
  AppliesToEmployeeType,
} from './types';

export const credentialKeys = {
  all: ['credential'] as const,
  myList: () => [...credentialKeys.all, 'me', 'list'] as const,
  myItem: (id: number) => [...credentialKeys.all, 'me', 'item', id] as const,
  employeeList: (employeeId: number) =>
    [...credentialKeys.all, 'employee', employeeId, 'list'] as const,
  requiredList: (appliesTo?: AppliesToEmployeeType) =>
    [...credentialKeys.all, 'required', appliesTo ?? 'all'] as const,
  myMappings: () => [...credentialKeys.all, 'me', 'mappings'] as const,
  myEligible: () => [...credentialKeys.all, 'me', 'eligible'] as const,
  adminMappings: (filters?: Record<string, unknown>) =>
    [...credentialKeys.all, 'admin', 'mappings', filters ?? {}] as const,
  employeeMappings: (employeeId: number) =>
    [...credentialKeys.all, 'employee', employeeId, 'mappings'] as const,
  compliance: (employeeId: number) =>
    [...credentialKeys.all, 'compliance', employeeId] as const,
};

export const useMyCredentials = () =>
  useQuery({
    queryKey: credentialKeys.myList(),
    queryFn: credentialApi.listMine,
    staleTime: 60_000,
  });

export const useEmployeeCredentials = (employeeId: number) =>
  useQuery({
    queryKey: credentialKeys.employeeList(employeeId),
    queryFn: () => credentialApi.listForEmployee(employeeId),
    enabled: !!employeeId,
    staleTime: 60_000,
  });

export const useCreateMyCredential = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateEmployeeCredentialRequest) => credentialApi.create(req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: credentialKeys.myList() });
      qc.invalidateQueries({ queryKey: credentialKeys.myEligible() });
      toast.success('Credential added');
    },
  });
};

export const useUpdateMyCredential = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: UpdateEmployeeCredentialRequest) => credentialApi.update(id, req),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: credentialKeys.myList() });
      qc.invalidateQueries({ queryKey: credentialKeys.myMappings() });
      if (data.affectedOrgs.length > 0) {
        const orgNames = data.affectedOrgs.map((o) => o.accountName).join(', ');
        toast.info(
          `Credential updated. Verification reset for ${data.affectedOrgs.length} org(s): ${orgNames}`
        );
      } else {
        toast.success('Credential updated');
      }
    },
  });
};

export const useDeleteMyCredential = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => credentialApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: credentialKeys.myList() });
      qc.invalidateQueries({ queryKey: credentialKeys.myMappings() });
      toast.success('Credential deleted');
    },
  });
};

export const useRequiredCredentials = (appliesTo?: AppliesToEmployeeType) =>
  useQuery({
    queryKey: credentialKeys.requiredList(appliesTo),
    queryFn: () => requiredCredentialApi.list(appliesTo),
    staleTime: 5 * 60_000,
  });

export const useCreateRequiredCredential = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateRequiredCredentialRequest) => requiredCredentialApi.create(req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: credentialKeys.requiredList() });
      toast.success('Requirement added');
    },
  });
};

export const useUpdateRequiredCredential = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: UpdateRequiredCredentialRequest) => requiredCredentialApi.update(id, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: credentialKeys.requiredList() });
      toast.success('Requirement updated');
    },
  });
};

export const useDeleteRequiredCredential = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => requiredCredentialApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: credentialKeys.requiredList() });
      toast.success('Requirement deleted');
    },
  });
};

export const useMyMappings = () =>
  useQuery({
    queryKey: credentialKeys.myMappings(),
    queryFn: credentialMappingApi.listMine,
  });

export const useMyEligibleRequirements = () => {
  const accountId = useAuthStore((s) => s.accountId);
  return useQuery({
    queryKey: credentialKeys.myEligible(),
    queryFn: credentialMappingApi.listMyEligibleRequirements,
    enabled: accountId !== null,
  });
};

export const useCreateMyMapping = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      credentialId,
      requirementId,
    }: {
      credentialId: number;
      requirementId: number;
    }) => credentialMappingApi.createMine(credentialId, requirementId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: credentialKeys.myList() });
      qc.invalidateQueries({ queryKey: credentialKeys.myMappings() });
      qc.invalidateQueries({ queryKey: credentialKeys.myEligible() });
      toast.success('Credential linked');
    },
  });
};

export const useDeleteMyMapping = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mappingId: number) => credentialMappingApi.deleteMine(mappingId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: credentialKeys.myMappings() });
      qc.invalidateQueries({ queryKey: credentialKeys.myEligible() });
      qc.invalidateQueries({ queryKey: credentialKeys.myList() });
      toast.success('Credential unlinked');
    },
  });
};

export const useEmployeeMappings = (employeeId: number) =>
  useQuery({
    queryKey: credentialKeys.employeeMappings(employeeId),
    queryFn: () => credentialMappingApi.listForEmployee(employeeId),
    enabled: !!employeeId,
  });

export const useVerifyMapping = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mappingId: number) => credentialMappingApi.verify(mappingId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: credentialKeys.all });
      toast.success('Credential verified');
    },
  });
};

export const useRejectMapping = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mappingId, reason }: { mappingId: number; reason: string }) =>
      credentialMappingApi.reject(mappingId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: credentialKeys.all });
      toast.success('Credential rejected');
    },
  });
};

export const useEmployeeCompliance = (employeeId: number) =>
  useQuery({
    queryKey: credentialKeys.compliance(employeeId),
    queryFn: () => credentialComplianceApi.forEmployee(employeeId),
    enabled: !!employeeId,
  });

export const useCredentialValidity = (guardUserIds: number[], throughDate: string | undefined) =>
  useQuery({
    queryKey: ['credential-validity', throughDate, [...guardUserIds].sort((a, b) => a - b)],
    queryFn: () => credentialValidityApi.check(guardUserIds, throughDate!),
    enabled: guardUserIds.length > 0 && !!throughDate,
    staleTime: 5 * 60 * 1000,
  });
